import _ from 'lodash';
import moment from 'moment';
import { Keys, locale } from 'src/i18n';
import {
  getConfigField,
  getDashboardSections,
  getFeaturesConfig,
  getLeadModules,
  getNonHierarchyRevyreModules,
  getRevyreModuleLabelByCode,
  getRevyreWorkflowConfig,
} from 'src/models/lmsConfig/queries';
// eslint-disable-next-line vymo-ui/restrict-import
import { REVYRE } from 'src/modules/revyre/constants';
import { getClientConfigData } from 'src/workspace/utils';
import CONSTANTS, { DEFAULT_PARAMS, FULL_PARAMS } from './constant';
import { SearchParams } from './search/types';

export function formatDateTimeToReadableEnglish(dateString) {
  const config = getClientConfigData();
  const clientLanguage = _.get(config, 'locale_configs.language') || 'en';

  function formatTime(date) {
    return moment(date).locale(clientLanguage).format('h:mm A');
  }

  function formatDate(date) {
    return moment(date).locale(clientLanguage).format('MMMM D, YYYY');
  }

  const date = moment(dateString);
  const now = moment();

  if (date.isSame(now, 'day')) {
    return `${locale(Keys.TODAY_AT)} ${formatTime(date)}`;
  }

  const yesterday = moment(now).subtract(1, 'days');
  if (date.isSame(yesterday, 'day')) {
    return `${locale(Keys.YESTERDAY_AT)} ${formatTime(date)}`;
  }

  return `${formatDate(date)} ${locale(Keys.AT)} ${formatTime(date)}`;
}

function isManageUsersEnabled() {
  const activeSections = getDashboardSections().filter(
    (section) => section.display !== false,
  );

  return _.find(activeSections, { type: 'manage-users' });
}

function isGlobalUserSearchConfigEnabled() {
  return getFeaturesConfig()?.user_search_config?.enable_global_search;
}

export function getModulesAndTypeForSearch() {
  const moduleList =
    isManageUsersEnabled() && isGlobalUserSearchConfigEnabled()
      ? [{ name: 'Users', code: 'user' }]
      : [];
  moduleList.push(
    getLeadModules()?.filter((module) => !(module.display === false)) || [],
  );

  const modulesExcludeFromSearch =
    getConfigField('exclude_modules_from_search') || [];

  const revyreModules = getRevyreWorkflowConfig().filter(
    (module) => module?.type !== 'HIERARCHY',
  );

  if (revyreModules.length) {
    return {
      type: 'revyre',
      modules: revyreModules,
    };
  }

  return {
    type: 'legacy',
    modules: _.filter(
      moduleList,
      (module) => !modulesExcludeFromSearch.includes(module.code),
    ),
  };
}

export const getModuleNameFromCode = (moduleCode) => {
  if (moduleCode === 'user') {
    return 'Users';
  }

  const revyreModules = getNonHierarchyRevyreModules() || [];
  if (revyreModules.length > 0) {
    return getRevyreModuleLabelByCode(moduleCode);
  }
  const modules = getLeadModules();
  const currentModule = _.find(modules, { code: moduleCode });
  const moduleName = _.get(currentModule, 'name');
  return moduleName;
};

export const getItemAttributesValues = (item) => {
  const attributes = item?.attributes || [];

  return attributes
    .filter((attr) => attr?.is_desc)
    .map((attr) => attr?.value)
    .filter(Boolean);
};

const { ACTION_CODES, ACTIVITY_ACTIONS, ACTIVITY_MAIN_ACTION_CODES } =
  CONSTANTS;

export const isCTAValidForWeb = (cta): boolean => {
  if (!cta?.code || !cta?.params) return false;

  if (!Object.values(ACTION_CODES).includes(cta.code)) {
    return false;
  }

  if (
    cta.code === ACTION_CODES.VIEW_DETAILS &&
    !Object.values(ACTIVITY_ACTIONS).includes(cta.params.activity)
  )
    return false;

  if (
    cta.code === ACTION_CODES.VIEW_DETAILS &&
    cta.params.activity === ACTIVITY_ACTIONS.MAIN &&
    !Object.values(ACTIVITY_MAIN_ACTION_CODES).includes(cta.params.code)
  )
    return false;

  return true;
};

export function getUserIntegrationAccounts() {
  const config = getClientConfigData();
  const integrations = _.get(config, 'features_config');
  const integrationTypes = {
    outlook_integration: () => _.get(config, 'outlook_config'),
    zoom_integration: () => _.get(config, 'zoom_config'),
    webex_integration: () => _.get(config, 'webex_config'),
    google_integration: () => _.get(config, 'google_config'),
  };

  return _.filter(
    _.keys(integrations),
    (feature) =>
      integrationTypes[feature] &&
      integrations[feature]?.enabled &&
      integrationTypes[feature](),
  );
}

export const getTitleByType = (type) => {
  let title = '';

  switch (type) {
    case 'outlook':
      title = locale(Keys.OFFICE_365);
      break;
    case 'zoom':
      title = locale(Keys.ZOOM);
      break;
    case 'webex':
      title = locale(Keys.WEBEX);
      break;
    case 'google':
      title = locale(Keys.GOOGLE);
      break;
    default:
      title = '';
  }
  return title;
};

export const buildParams = (
  selectedModule: string,
  full: boolean,
): SearchParams => {
  const params: SearchParams = full
    ? { ...FULL_PARAMS }
    : { ...DEFAULT_PARAMS };

  if (selectedModule !== 'all' && full) {
    params.modules_code = selectedModule;
  }

  return params;
};

export const finalSelectedTags = (checked, selectedModule = '') => {
  const selectedTags = selectedModule.split(',').filter(Boolean);
  let nextSelectedTags = selectedTags.includes(checked.code)
    ? selectedTags.filter((t) => t !== checked.code)
    : [...selectedTags, checked.code];

  if (checked.code === 'all' && checked) {
    nextSelectedTags = ['all'];
  }

  if (nextSelectedTags.length > 1 && nextSelectedTags.includes('all')) {
    nextSelectedTags = nextSelectedTags.filter((t) => t !== 'all');
  }

  if (nextSelectedTags.length === 0) {
    nextSelectedTags = ['all'];
  }

  return nextSelectedTags.join(',');
};

export const getTransformedSearchResults = (results = []) =>
  results.map((result: any = {}) => ({
    ...(result.mutatedEntity || {}),
    module_code: result.workflowType,
    ...result,
    highlight: {},
  }));

export const globalActions = (): { name: string; code: string }[] => {
  const revyreGlobalWorkflowModules = getNonHierarchyRevyreModules().filter(
    (module) =>
      !REVYRE.COLLECT_IQ.CREATE_SCENARIO_BLOCKED_ENTITIES.includes(
        module.code,
      ) &&
      (module.display ?? true),
  );

  const baseActions = [
    { name: locale(Keys.CREATE_ACTIVITY), code: 'create_task' },
    { name: locale(Keys.CREATE_NEW_RECORD), code: 'add_lead' },
    { name: locale(Keys.CREATE_NEW_USER), code: 'add_user' },
  ];

  if (!revyreGlobalWorkflowModules.length) {
    return baseActions;
  }

  const workflowActions = revyreGlobalWorkflowModules.map((module) => ({
    name: `Create ${module.name}`,
    code: module.code,
  }));

  return [...workflowActions, baseActions[2]];
};
